import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";

import {RosterAPI} from "@/api/roster";
import {SeasonAPI} from "@/api/season";
import {TeamAPI} from "@/api/team";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	REQUIRED_FIELD,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const ManagerHandleFootballTeamPage = (props) => {
	const {edit, handleNotification, source = "manager"} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const basePath =
		source === "manager" ? "/manager/teams" : "/development/rosters";

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});

	const [seasons, setSeasons] = useState([]);
	const getSeasons = async () => {
		try {
			const response = await SeasonAPI.allSeasons();
			setSeasons(
				response.map((s) => {
					return {
						value: s.id,
						label: s.name,
					};
				}),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const [teams, setTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await TeamAPI.list();
			setTeams(
				response.map((s) => {
					return {
						value: s.id,
						label: s.name,
					};
				}),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		getSeasons();
		if (source === "manager") {
			getTeams();
		}
	}, [source]);

	const getTeam = async () => {
		setLoading(true);
		try {
			const response = await RosterAPI.getById(id);
			response.currentSeason = {
				value: response.season?.id,
				label: response.season?.name,
			};
			if (response.team) {
				response.currentTeam = {
					value: response.team?.id,
					label: response.team?.name,
				};
			}
			response.currentCategory = {
				value: response.playerCategory?.id,
				label: response.playerCategory?.name,
			};
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getTeam();
		}
	}, [edit]);

	const getRequiredFields = () => {
		return source === "manager"
			? ["currentSeason", "currentTeam", "currentCategory"]
			: ["currentSeason", "currentCategory"];
	};

	const saveTeam = async () => {
		try {
			const errors = checkRequiredFields(getRequiredFields(), fields);
			setErrors(errors);

			if (!errors.length) {
				setLoading(true);

				let body = Object.assign({}, fields);

				body = {
					...fields,
					seasonId: fields.currentSeason.value,
					playerCategoryId: fields.currentCategory.value,
					name: "",
					prospect: source === "scouting",
				};

				if (source === "manager") {
					body.teamId = fields.currentTeam.value;
				} else {
					body.teamId = "afce26c1-5168-4c3c-b0df-e3f9f3a6a011";
				}

				const response = edit
					? await RosterAPI.update(id, body)
					: await RosterAPI.create(body);

				setTimeout(() => {
					navigate(`${basePath}/${response?.id || id}`);
					setLoading(false);
					handleNotification(SUCCESSFUL_OPERATION, "success");
				}, 1000);
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveTeam();
	};

	const goBack = () => {
		if (edit) {
			navigate(basePath + "/" + id);
		} else {
			navigate(basePath);
		}
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const label = fields.currentSeason
		? fields.currentSeason?.label + " " + fields.currentCategory?.label
		: "";
	// source === "scouting"
	// 	? fields?.name
	// 	: fields?.name + " " + fields?.team?.name;

	const breadcrumbsElements = [
		{label: source === "scouting" ? "Roster" : "Rose", path: basePath},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: label,
				path: basePath + "/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<VLFCard
					title="Informazioni principali"
					content={
						<div className="row no-margin">
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={seasons}
									sortOrder="descend"
									value={fields.currentSeason}
									onChange={(newValue) => {
										setErrors([]);
										setFields({...fields, currentSeason: newValue});
									}}
									label="Stagione"
									required
									helperText={
										errors.indexOf("currentSeason") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("currentSeason") !== -1}
								/>
							</div>
							{source === "manager" && (
								<div className="col-sm-12 col-md-6 padding-small">
									<VLFSelectNew
										options={teams}
										sortOrder="descend"
										value={fields.currentTeam}
										onChange={(newValue) => {
											setErrors([]);
											setFields({...fields, currentTeam: newValue});
										}}
										label="Squadra"
										required
										helperText={
											errors.indexOf("currentTeam") !== -1 ? REQUIRED_FIELD : ""
										}
										error={errors.indexOf("currentTeam") !== -1}
									/>
								</div>
							)}
							<div className="col-sm-12 col-md-6 padding-small">
								<VLFSelectNew
									options={categories.filter(
										(c) => c.label !== "Prima Squadra",
									)}
									sorted={false}
									value={fields.currentCategory}
									onChange={(newValue) => {
										setErrors([]);
										setFields({...fields, currentCategory: newValue});
									}}
									label="Categoria"
									required
									helperText={
										errors.indexOf("currentCategory") !== -1
											? REQUIRED_FIELD
											: ""
									}
									error={errors.indexOf("currentCategory") !== -1}
								/>
							</div>
						</div>
					}
				/>
			</div>
		</Page>
	);
};

export default ManagerHandleFootballTeamPage;
